import React from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { TrendingUp, ShieldCheck, BarChart3 } from "lucide-react";

export default function Login() {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center label-overline">Loading…</div>;
  }
  if (user) return <Navigate to="/dashboard" replace />;

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="theme-light min-h-screen grid grid-cols-1 lg:grid-cols-2" style={{ background: "var(--bg-base)", color: "var(--text-primary)" }}>
      {/* Left: brand & copy */}
      <div className="flex flex-col justify-between p-8 md:p-12 lg:p-16" style={{ background: "var(--bg-base)" }}>
        <div className="flex items-center">
          <img
            src="/brand/logo-light.png"
            alt="EquityBox — Vedansh Wealth LLP"
            className="h-10 w-auto"
            style={{ maxHeight: 40 }}
            data-testid="login-brand-logo"
          />
        </div>

        <div className="max-w-lg">
          <p className="label-overline mb-6">Portfolio Tracker · NSE / BSE</p>
          <h1 className="font-display text-[34px] sm:text-[44px] font-bold tracking-[-0.035em] leading-[1.05]">
            Your shelf of equities,<br />
            <span style={{ color: "var(--accent-primary)" }}>calmly accounted for.</span>
          </h1>
          <p className="mt-6 text-base leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            Track holdings across NSE & BSE in INR. See your absolute gain composed of unrealized
            gains, realized profits and dividends — measured against your real, deployed capital.
          </p>

          <div className="mt-12 space-y-4">
            <Feature icon={<BarChart3 className="w-4 h-4" strokeWidth={1.75} />} label="Unrealized · Realized · Dividends" />
            <Feature icon={<TrendingUp className="w-4 h-4" strokeWidth={1.75} />} label="True cost basis from first transaction" />
            <Feature icon={<ShieldCheck className="w-4 h-4" strokeWidth={1.75} />} label="Private. Encrypted. Yours." />
          </div>

          <button
            data-testid="google-login-button"
            onClick={handleLogin}
            className="mt-12 inline-flex items-center gap-3 rounded px-6 py-3.5 font-medium text-sm border transition-colors duration-200 hover:opacity-90"
            style={{
              background: "var(--text-primary)",
              color: "white",
              borderColor: "var(--text-primary)",
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#fff"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.836.86-3.048.86-2.344 0-4.328-1.583-5.036-3.71H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#fff" opacity=".9"/>
              <path d="M3.964 10.708A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.708V4.96H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.04l3.007-2.332z" fill="#fff" opacity=".7"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.96L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#fff" opacity=".55"/>
            </svg>
            Continue with Google
          </button>
          <p className="mt-4 text-xs" style={{ color: "var(--text-muted)" }}>
            By signing in you agree to track your portfolio responsibly.
          </p>
        </div>

        <div className="text-xs" style={{ color: "var(--text-muted)" }}>
          © {new Date().getFullYear()} EquityBox Portfolio
        </div>
      </div>

      {/* Right: image */}
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1602128110234-2d11c0aaadfe?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: "rgba(15,28,40,0.35)" }} />
        <div className="absolute bottom-10 left-10 right-10 text-white max-w-md">
          <p className="label-overline" style={{ color: "rgba(255,255,255,0.85)" }}>A patient instrument</p>
          <p className="mt-2 font-display text-2xl tracking-tight leading-snug">
            "The market is a voting machine in the short run, but a weighing machine in the long run."
          </p>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon, label }) {
  return (
    <div className="flex items-center gap-3 text-sm" style={{ color: "var(--text-secondary)" }}>
      <span className="w-8 h-8 rounded flex items-center justify-center" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-soft)" }}>
        {icon}
      </span>
      {label}
    </div>
  );
}
